import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Upload,
  FileText,
  FileArchive,
  Trash2,
  History,
  X,
  Play,
  FileDown,
  Code2,
} from "lucide-react";

const SAMPLE = `^XA
^FO50,50^A0N,40,40^FDExemplo - cole aqui o ZPL do marketplace^FS
^FO50,120^A0N,30,30^FDAceita ^GF (inline) e ^DG/^XG (Mercado Livre).^FS
^FO50,170^A0N,30,30^FDOu use o upload (.txt / .zpl / .prn / .zip).^FS
^XZ`;

// Rough per-label render time (ms). Thermal renders ~10-25ms/label after
// parallelization, A4 ~30-60ms due to image assembly + reportlab.
// Used only for the estimated progress bar animation.
const MS_PER_LABEL = { thermal: 15, a4: 45 };

export default function ZplConverter() {
  const [zpl, setZpl] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [labelCount, setLabelCount] = useState(null);
  const [outputFormat, setOutputFormat] = useState("thermal"); // "thermal" | "a4"
  const [method, setMethod] = useState("native"); // "native" | "labelary"
  const [history, setHistory] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  // Staged files awaiting the user's click on "Converter".
  const [stagedFile, setStagedFile] = useState(null);
  const [progress, setProgress] = useState(0);          // 0..100
  const [progressLabel, setProgressLabel] = useState(""); // "Preparando…" / "Renderizando 234/500…"
  const [progressDone, setProgressDone] = useState(0);   // e.g. 45
  const [progressTotal, setProgressTotal] = useState(0); // e.g. 500
  const progressTimer = useRef(null);
  const fileRef = useRef(null);

  const loadHistory = async () => {
    setHistoryBusy(true);
    try {
      const resp = await api.get("/labels/history");
      setHistory(Array.isArray(resp.data) ? resp.data : []);
    } catch (_e) {
      // silent — history is a nice-to-have
    } finally {
      setHistoryBusy(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const handlePdfBlob = (blob, count) => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    const url = URL.createObjectURL(blob);
    setPdfUrl(url);
    setLabelCount(count);
  };

  const readErrorMessage = async (e) => {
    let msg = "Falha na conversão";
    try {
      if (e.response?.data) {
        const text = await e.response.data.text();
        msg = JSON.parse(text).detail || msg;
      } else if (e.message) {
        msg = e.message;
      }
    } catch (_e) { /* ignore */ }
    return msg;
  };

  const startProgressAnimation = (estimatedLabels) => {
    const totalMs = Math.max(1500, estimatedLabels * MS_PER_LABEL[outputFormat]);
    const startedAt = Date.now();
    setProgress(2);
    setProgressLabel("Preparando arquivos…");
    if (progressTimer.current) clearInterval(progressTimer.current);
    progressTimer.current = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const raw = (elapsed / totalMs) * 95;
      const clamped = Math.min(95, Math.round(raw));
      setProgress(clamped);
      if (elapsed > 400 && elapsed < totalMs * 0.4) {
        setProgressLabel("Renderizando etiquetas…");
      } else if (elapsed >= totalMs * 0.4 && elapsed < totalMs * 0.85) {
        setProgressLabel("Compondo PDF final…");
      } else if (elapsed >= totalMs * 0.85) {
        setProgressLabel("Finalizando…");
      }
    }, 120);
  };

  const stopProgressAnimation = (final = 100) => {
    if (progressTimer.current) {
      clearInterval(progressTimer.current);
      progressTimer.current = null;
    }
    setProgress(final);
    setTimeout(() => {
      setProgress(0);
      setProgressLabel("");
      setProgressDone(0);
      setProgressTotal(0);
    }, 800);
  };

  /**
   * Poll the async-job endpoint until the conversion is done.
   * Updates the exact `X of Y` counter + free-form message returned by
   * the backend, so the progress bar is *real* (server-driven), not a
   * client-side estimate.
   */
  const pollJob = async (jobId) => {
    const start = Date.now();
    let consecutive404 = 0;
    while (true) {
      // 500ms poll — fine-grained enough that the counter feels live
      // without hammering the API (a 500-label labelary run takes ~2min
      // = ~240 polls, well within tolerance).
      await new Promise((r) => setTimeout(r, 500));
      let s;
      try {
        s = (
          await api.get(`/labels/jobs/${jobId}`, { timeout: 30000 })
        ).data;
        consecutive404 = 0;
      } catch (e) {
        // A stray 404 can happen right after the POST returns while
        // Mongo is still consistent — tolerate a handful before we
        // give up. Everything else = transient network, keep retrying.
        if (e.response?.status === 404) {
          consecutive404 += 1;
          if (consecutive404 > 20) {
            throw new Error("Job não encontrado no servidor");
          }
        }
        if (Date.now() - start > 20 * 60 * 1000) {
          throw new Error("Timeout: conversão levou mais de 20 minutos");
        }
        continue;
      }
      const total = Math.max(1, s.total || 1);
      const done = Math.min(total, s.done || 0);
      setProgressDone(done);
      setProgressTotal(total);
      setProgress(Math.min(99, Math.round((done / total) * 100)));
      setProgressLabel(s.message || "Convertendo…");
      if (s.status === "done") return s;
      if (s.status === "error") throw new Error(s.error || "Falha na conversão");
    }
  };

  const convert = async (format = outputFormat) => {
    setOutputFormat(format);
    setBusy(true);
    setProgress(2);
    setProgressLabel("Preparando…");
    try {
      // -----------------------------------------------------------------
      // Path A — paste-only ZPL (no file). Keep it on the fast sync
      // endpoint; async job would be overkill for a single label.
      // -----------------------------------------------------------------
      if (!stagedFile) {
        const resp = await api.post(
          `/labels/zpl-to-pdf?format=${format}`,
          { zpl },
          { responseType: "blob" }
        );
        const dur = parseInt(resp.headers["x-duration-ms"] || "0", 10);
        handlePdfBlob(resp.data, 1);
        toast.success(
          (format === "a4"
            ? "ZPL convertido em folha A4 (4 por página)"
            : "ZPL convertido") +
          (dur > 0 ? ` · ${formatDuration(dur)}` : "")
        );
        loadHistory();
        stopProgressAnimation(100);
        return;
      }

      // -----------------------------------------------------------------
      // Path B — file upload → async job with server-driven progress.
      // -----------------------------------------------------------------
      const fd = new FormData();
      fd.append("file", stagedFile);
      const { data: startResp } = await api.post(
        `/labels/convert-async?format=${format}&method=${method}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          // 5 min timeout for the initial upload — big Shopee zips can
          // take a while over slow uplinks. The job itself runs after
          // the POST returns so this timeout only bounds the upload.
          timeout: 300000,
          // Explicit 32 MB body cap on the client side matches the
          // Cloudflare/ingress limits, so we fail fast if a user
          // tries to upload something absurd.
          maxContentLength: 32 * 1024 * 1024,
          maxBodyLength: 32 * 1024 * 1024,
        }
      );
      const jobId = startResp.job_id;
      const finalStatus = await pollJob(jobId);

      // Download the final PDF blob using the job's dedicated endpoint.
      // Retry once on transient network failure — big PDFs can hit
      // a proxy timeout on the first byte-transfer attempt.
      let dl;
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          dl = await api.get(`/labels/jobs/${jobId}/download`, {
            responseType: "blob",
            timeout: 600000, // 10 min — plenty for 20+ MB PDFs on slow uplinks
          });
          break;
        } catch (err) {
          if (attempt === 2) throw err;
          setProgressLabel("Baixando PDF final… (retry)");
          await new Promise((r) => setTimeout(r, 800));
        }
      }
      const count = parseInt(dl.headers["x-label-count"] || finalStatus.total || "1", 10);
      const dur = parseInt(dl.headers["x-duration-ms"] || finalStatus.duration_ms || "0", 10);
      handlePdfBlob(dl.data, count);
      const suffix = format === "a4" ? " em A4" : "";
      const methodLabel = method === "labelary" ? " · Labelary" : "";
      toast.success(
        `${count} etiqueta${count > 1 ? "s" : ""} convertida${count > 1 ? "s" : ""}${suffix}${methodLabel}` +
        (dur > 0 ? ` · ${formatDuration(dur)}` : "")
      );
      loadHistory();
      setStagedFile(null);
      stopProgressAnimation(100);
    } catch (e) {
      stopProgressAnimation(0);
      const msg = e.response?.data?.detail || (await readErrorMessage(e));
      toast.error(msg);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const estimateLabelCount = async (file) => {
    try {
      if (file.name.toLowerCase().endsWith(".zip")) return 100;
      const text = await file.text();
      const matches = text.match(/\^XA/g);
      return matches ? matches.length : 1;
    } catch (_e) {
      return 1;
    }
  };

  const stageFile = (file) => {
    if (!file) return;
    const allowed = [".txt", ".zpl", ".prn", ".zip"];
    const name = file.name.toLowerCase();
    if (!allowed.some((ext) => name.endsWith(ext))) {
      toast.error(`Formato não suportado. Aceito: ${allowed.join(", ")}`);
      return;
    }
    setStagedFile(file);
    toast.success(`Arquivo pronto: ${file.name}`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) stageFile(file);
  };

  const downloadCurrentPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `etiquetas-${outputFormat}-${Date.now()}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadFromHistory = async (item) => {
    try {
      const resp = await api.get(`/labels/history/${item.id}/download`, {
        responseType: "blob",
        timeout: 300000,
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const base = (item.filename || "labels").replace(/\.[^.]+$/, "");
      a.download = `labels-${base}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(await readErrorMessage(e));
    }
  };

  const deleteFromHistory = async (item) => {
    if (!window.confirm(`Remover "${item.filename}" do histórico?`)) return;
    try {
      await api.delete(`/labels/history/${item.id}`);
      setHistory((prev) => prev.filter((h) => h.id !== item.id));
      toast.success("Removido do histórico");
    } catch (e) {
      toast.error(await readErrorMessage(e));
    }
  };

  const formatSize = (n) => {
    if (!n) return "-";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const formatDuration = (ms) => {
    if (!ms || ms < 0) return "-";
    if (ms < 1000) return `${ms} ms`;
    const s = ms / 1000;
    if (s < 60) return `${s.toFixed(s < 10 ? 2 : 1)} s`;
    const m = Math.floor(s / 60);
    const rem = Math.round(s - m * 60);
    return `${m}m ${rem}s`;
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch (_e) {
      return iso;
    }
  };

  const daysRemaining = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "expirando";
    const days = Math.floor(ms / (24 * 3600 * 1000));
    const hours = Math.floor((ms % (24 * 3600 * 1000)) / (3600 * 1000));
    if (days === 0) return `${hours}h`;
    return `${days}d`;
  };

  const hasSomething = zpl.trim().length > 0 || stagedFile;
  const charCount = zpl.length;

  return (
    <div className="mx-auto max-w-6xl" data-testid="zpl-page">
      {/* -------- Header row (badge + big title) --------- */}
      <div className="mb-8">
        <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-[11px] font-semibold text-blue-700">
          Converter
        </span>
        <h1 className="mt-4 text-5xl font-black tracking-tighter text-slate-900">
          ZPL <span className="text-slate-400 font-normal">→</span> PDF
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-lg">
          Cole, solte ou envie um <span className="font-mono">.zip</span> — recebe o PDF instantaneamente.
        </p>
      </div>

      {/* -------- Two-column card grid: input + preview --------------- */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* LEFT CARD — upload + paste */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          {/* Upload dropzone */}
          <div
            data-testid="zpl-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/70 p-6 transition hover:border-slate-300 hover:bg-slate-50"
          >
            {stagedFile ? (
              <div className="flex items-center justify-between gap-3 rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  {stagedFile.name.toLowerCase().endsWith(".zip") ? (
                    <FileArchive className="h-4 w-4 flex-shrink-0 text-slate-500" />
                  ) : (
                    <FileText className="h-4 w-4 flex-shrink-0 text-slate-500" />
                  )}
                  <span className="truncate font-mono text-xs text-slate-800" title={stagedFile.name}>
                    {stagedFile.name}
                  </span>
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">
                    {formatSize(stagedFile.size)}
                  </span>
                </div>
                <button
                  data-testid="zpl-clear-staged"
                  onClick={() => { setStagedFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  title="Remover arquivo"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label
                className="flex flex-col items-center justify-center cursor-pointer text-center"
                data-testid="zpl-upload-label"
              >
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-white border border-slate-200 shadow-sm">
                  <Upload className="h-5 w-5 text-slate-600" />
                </div>
                <div className="text-sm font-medium text-slate-900">
                  Arraste um arquivo ou clique pra escolher
                </div>
                <div className="mt-1 text-xs text-slate-500 font-mono">
                  .txt · .zpl · .prn · .zip
                </div>
                <input
                  ref={fileRef}
                  data-testid="zpl-file-input"
                  type="file"
                  className="hidden"
                  accept=".txt,.zpl,.prn,.zip"
                  onChange={(e) => stageFile(e.target.files?.[0])}
                />
              </label>
            )}
          </div>

          {/* Paste ZPL text area */}
          <div className="mt-5">
            <div className="mb-2 flex items-center gap-1.5">
              <Code2 className="h-3.5 w-3.5 text-slate-400" />
              <label htmlFor="zpl-textarea" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Cole seu ZPL
              </label>
              {charCount > 0 && (
                <span className="ml-auto text-[10px] text-slate-400 font-mono">
                  {charCount.toLocaleString("pt-BR")} chars
                </span>
              )}
            </div>
            <textarea
              id="zpl-textarea"
              data-testid="zpl-textarea"
              value={zpl}
              onChange={(e) => setZpl(e.target.value)}
              spellCheck={false}
              rows={9}
              className="w-full resize-y rounded-lg border border-slate-200 bg-slate-50/50 p-3 font-mono text-[11px] leading-relaxed text-slate-700 outline-none transition placeholder:text-slate-400 placeholder:italic focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
              placeholder={SAMPLE}
            />
          </div>

          {/* Method selector — only relevant to file uploads */}
          <div className="mt-4">
            <div className="mb-2 flex items-center gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Método de conversão
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="zpl-method-selector">
              <label
                data-testid="zpl-method-native"
                className={
                  "flex cursor-pointer flex-col rounded-lg border p-3 transition " +
                  (method === "native"
                    ? "border-blue-600 bg-blue-50/50 ring-1 ring-blue-200"
                    : "border-slate-200 bg-white hover:border-slate-300")
                }
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="zpl-method"
                    value="native"
                    checked={method === "native"}
                    onChange={() => setMethod("native")}
                    className="h-3.5 w-3.5"
                  />
                  <span className="text-xs font-bold text-slate-900">
                    Conversão interna (nativa)
                  </span>
                </div>
                <span className="mt-1 pl-5 text-[10px] leading-relaxed text-slate-500">
                  Local, ilimitado. Ideal para marketplaces com <span className="font-mono">^GF</span>.
                </span>
              </label>
              <label
                data-testid="zpl-method-labelary"
                className={
                  "flex cursor-pointer flex-col rounded-lg border p-3 transition " +
                  (method === "labelary"
                    ? "border-blue-600 bg-blue-50/50 ring-1 ring-blue-200"
                    : "border-slate-200 bg-white hover:border-slate-300")
                }
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="zpl-method"
                    value="labelary"
                    checked={method === "labelary"}
                    onChange={() => setMethod("labelary")}
                    className="h-3.5 w-3.5"
                  />
                  <span className="text-xs font-bold text-slate-900">
                    Conversão rápida (Labelary)
                  </span>
                </div>
                <span className="mt-1 pl-5 text-[10px] leading-relaxed text-slate-500">
                  API externa, lotes de 15. Fallback nativo automático se algo falhar.
                </span>
              </label>
            </div>
          </div>

          {/* Progress bar */}
          {progress > 0 && (
            <div className="mt-4" data-testid="zpl-progress">
              <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <span data-testid="zpl-progress-label">
                  {progressLabel || "Convertendo…"}
                </span>
                <span className="font-mono" data-testid="zpl-progress-count">
                  {progressTotal > 1
                    ? `${progressDone} de ${progressTotal} · ${progress}%`
                    : `${progress}%`}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-blue-600 transition-[width] duration-200 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* CTA row */}
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              data-testid="zpl-convert-thermal-btn"
              onClick={() => convert("thermal")}
              disabled={busy || !hasSomething}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0f2b6c] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a1f4f] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy && outputFormat === "thermal" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5 fill-current" />
              )}
              Converter ZPL para PDF
            </button>
            <button
              data-testid="zpl-convert-a4-btn"
              onClick={() => convert("a4")}
              disabled={busy || !hasSomething}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy && outputFormat === "a4" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="text-slate-500">✂</span>
              )}
              Converter ZPL em A4
            </button>
          </div>

          <p className="mt-4 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
            <span className="mt-0.5 text-slate-400">ⓘ</span>
            <span>
              <b>PDF térmico</b>: 1 etiqueta por página, tamanho da impressora.{" "}
              <b>A4</b>: 4 etiquetas por folha com linhas pontilhadas pra recortar.
            </span>
          </p>
        </div>

        {/* RIGHT CARD — preview */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <FileText className="h-4 w-4 text-slate-500" />
              <span className="font-semibold">Pré-visualização</span>
              {labelCount != null && (
                <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-600">
                  {labelCount} etq
                </span>
              )}
            </div>
            <button
              data-testid="zpl-download-current"
              onClick={downloadCurrentPdf}
              disabled={!pdfUrl}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FileDown className="h-3.5 w-3.5" />
              Baixar PDF
            </button>
          </div>
          <div className="flex-1 min-h-[520px] bg-slate-100">
            {pdfUrl ? (
              <iframe
                data-testid="zpl-preview-iframe"
                key={pdfUrl}
                src={pdfUrl}
                title="PDF preview"
                className="h-full w-full min-h-[520px] border-0"
              />
            ) : (
              <div className="flex h-full min-h-[520px] flex-col items-center justify-center gap-3 p-8 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white border border-slate-200 shadow-sm">
                  <FileText className="h-5 w-5 text-slate-400" />
                </div>
                <div className="text-sm font-medium text-slate-700">
                  O preview aparece aqui após converter
                </div>
                <div className="text-xs text-slate-400 max-w-xs">
                  Cole o ZPL ou arraste um arquivo e clique em <b>Converter</b>.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* -------- Download history -------- */}
      <div className="mt-10 rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="zpl-history-card">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-800">Histórico de conversões</h2>
            <span className="ml-1 text-[11px] text-slate-400">
              (7 dias · retenção automática)
            </span>
          </div>
          <button
            data-testid="zpl-history-refresh"
            onClick={loadHistory}
            disabled={historyBusy}
            className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800 disabled:opacity-50"
          >
            {historyBusy ? "Atualizando…" : "Atualizar"}
          </button>
        </div>
        {history.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500">
            Ainda não há conversões salvas. Converta um arquivo e ele aparecerá aqui pra você baixar novamente sem reprocessar.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {history.map((it) => (
              <li
                key={it.id}
                data-testid={`zpl-history-row-${it.id}`}
                className="flex items-center justify-between gap-3 px-5 py-3 transition hover:bg-slate-50/60"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {it.filename?.toLowerCase().endsWith(".zip") ? (
                    <FileArchive className="h-4 w-4 flex-shrink-0 text-slate-400" />
                  ) : (
                    <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate font-mono text-xs text-slate-800"
                      title={it.filename || "sem-nome"}
                    >
                      {it.filename || "sem-nome"}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                      <span>{formatDate(it.created_at)}</span>
                      <span>· {it.label_count || 1} etq</span>
                      <span>· {(it.format || "thermal").toUpperCase()}</span>
                      <span>· {formatSize(it.size_bytes || it.size)}</span>
                      {it.duration_ms ? (
                        <span
                          data-testid={`zpl-history-duration-${it.id}`}
                          className="text-blue-600"
                          title={`${it.duration_ms} ms total · média ${it.ms_per_label || 0} ms por etiqueta`}
                        >
                          · ⏱ {formatDuration(it.duration_ms)}
                          {it.label_count > 1 && it.ms_per_label ? ` (${it.ms_per_label}ms/etq)` : ""}
                        </span>
                      ) : null}
                      {it.expires_at && (
                        <span className="text-emerald-600">
                          · expira em {daysRemaining(it.expires_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`zpl-history-download-${it.id}`}
                    onClick={() => downloadFromHistory(it)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                    title="Baixar PDF"
                  >
                    <FileDown className="h-3.5 w-3.5" />
                    Baixar
                  </button>
                  <button
                    data-testid={`zpl-history-delete-${it.id}`}
                    onClick={() => deleteFromHistory(it)}
                    className="rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                    title="Remover do histórico"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
